import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { Tooltip } from "antd";
import DonutPlot from "@/components/CategoricalPlots";
import FetchWrapper from "@/components/UI/FetchWraper";
import BrokenLinkIcon from "@/assets/icons/broken_link.svg?react";

/**
 * Purpose-built RECIST charts for Analysis > Tumor Response.
 *
 * Fetches every tumor_response row for the trial (workspace filters are ignored,
 * as with the AE Metrics tab) and renders:
 *   1. an ORR / DCR / confirmed summary (donuts + BOR breakdown),
 *   2. a waterfall of best % change coloured by best overall response, with the
 *      +20% (PD) and -30% (PR) RECIST reference lines,
 *   3. a progression-free-survival swimlane, with an ongoing-treatment marker.
 */

type TumorRow = {
  subject?: string | null;
  best_overall_response?: string | null;
  best_pct_change?: number | null;
  baseline_sld_mm?: number | null;
  pfs_days?: number | null;
  confirmed?: boolean | null;
  treatment_ongoing?: boolean | null;
  tumor_type?: string | null;
  part?: string | null;
  b7h3_expression?: string | null;
};

// RECIST best-overall-response colour scale (also used by both charts + legend).
const BOR_ORDER = ["CR", "PR", "SD", "PD", "NE"] as const;
// On-brand ordinal ramp (deep green → green → blue → pink → grey), mirroring
// MOLRESP_COLORS in ../Biomarkers/charts.tsx. Deliberately not a red/amber/green
// traffic light: those hues are off-brand for Vivo.
const BOR_COLORS: Record<string, string> = {
  CR: "#1C4A36",
  PR: "#7DC199",
  SD: "#64BDE1",
  PD: "#C275A1",
  NE: "#BEBEBE",
};
const BOR_LABELS: Record<string, string> = {
  CR: "Complete Response",
  PR: "Partial Response",
  SD: "Stable Disease",
  PD: "Progressive Disease",
  NE: "Not Evaluable",
};
const borColor = (bor?: string | null) =>
  (bor && BOR_COLORS[bor.trim().toUpperCase()]) || "#9aa5ab";

const payload = {
  dataset_type: "tumor_response",
  global_filters: [],
  pagination: { page: 1, page_size: 10000 },
};

/** Measure a container's width so the SVG charts stay responsive. */
function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function TumorResponseCharts() {
  return (
    <FetchWrapper endpointSuffix="filters/data/" method="POST" body={payload}>
      {(data) => (
        <TumorResponseChartsInner data={(data?.results ?? []) as TumorRow[]} />
      )}
    </FetchWrapper>
  );
}

function TumorResponseChartsInner({ data }: { readonly data: TumorRow[] }) {
  const summary = useMemo(() => computeSummary(data), [data]);

  return (
    <div className="mt-5 flex flex-col gap-6">
      <BorSummary summary={summary} />
      <WaterfallChart data={data} />
      <PfsSwimlane data={data} />
      <BorLegend />
    </div>
  );
}

/* ------------------------------------------------------------------ summary */

type Summary = {
  evaluable: number;
  counts: Record<string, number>;
  orr: number; // CR + PR
  dcr: number; // CR + PR + SD
  confirmed: number;
};

function computeSummary(rows: TumorRow[]): Summary {
  const counts: Record<string, number> = { CR: 0, PR: 0, SD: 0, PD: 0, NE: 0 };
  let confirmed = 0;
  for (const r of rows) {
    const bor = (r.best_overall_response ?? "").trim().toUpperCase();
    if (bor in counts) counts[bor] += 1;
    if (r.confirmed === true && (bor === "CR" || bor === "PR")) confirmed += 1;
  }
  const evaluable = rows.length;
  const orr = counts.CR + counts.PR;
  const dcr = counts.CR + counts.PR + counts.SD;
  return { evaluable, counts, orr, dcr, confirmed };
}

const DONUT_DIMENSIONS = { width: 200, height: 200, margin: 36 };
const DONUT_CARD_CLASS =
  "bg-white rounded-xs relative shadow-omni h-[230px] flex items-center justify-center";

function BorSummary({ summary }: { readonly summary: Summary }) {
  const { evaluable, orr, dcr, confirmed, counts } = summary;
  const donuts = [
    {
      className: "orr tumor-response",
      text: ["Overall Response", "Rate (ORR)"],
      data: {
        "Responders (CR+PR)": orr,
        "Non-responders": Math.max(0, evaluable - orr),
      },
      valueOfInterest: "Responders (CR+PR)",
      centerLabel:
        "Confirmed + unconfirmed CR or PR as a share of evaluable subjects",
    },
    {
      className: "dcr tumor-response",
      text: ["Disease Control", "Rate (DCR)"],
      data: {
        "Disease control (CR+PR+SD)": dcr,
        "Progression / NE": Math.max(0, evaluable - dcr),
      },
      valueOfInterest: "Disease control (CR+PR+SD)",
      centerLabel: "CR, PR or SD as a share of evaluable subjects",
    },
    {
      className: "confirmed tumor-response",
      text: ["Confirmed", "Responses"],
      data: {
        "Confirmed CR/PR": confirmed,
        "Not confirmed": Math.max(0, orr - confirmed),
      },
      valueOfInterest: "Confirmed CR/PR",
      centerLabel: "Responders with a confirmatory scan, out of all CR/PR",
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Best Overall Response summary"
        subtitle={`${evaluable} evaluable subjects (RECIST v1.1)`}
      />
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-[15px]">
        {donuts.map((cfg) => (
          <div key={cfg.className} className={DONUT_CARD_CLASS}>
            <span className="absolute top-2 right-2">
              <Tooltip title="Workspace filters are ignored for this summary">
                <span className="inline-flex">
                  <BrokenLinkIcon className="w-4 h-4 cursor-default text-[#2B4752]" />
                </span>
              </Tooltip>
            </span>
            <DonutPlot
              addPercent
              className={cfg.className}
              text={cfg.text}
              data={cfg.data as unknown as Record<string, number>}
              dimensions={DONUT_DIMENSIONS}
              valueOfInterest={cfg.valueOfInterest}
              centerLabel={cfg.centerLabel}
            />
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {BOR_ORDER.map((bor) => (
          <span
            key={bor}
            className="inline-flex items-center gap-2 rounded-xs bg-white shadow-omni px-3 py-1 text-sm"
          >
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: BOR_COLORS[bor] }}
            />
            <span className="font-medium">{bor}</span>
            <span className="text-[#5b6b73]">{counts[bor]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- waterfall */

const WF_MARGIN = { top: 24, right: 16, bottom: 28, left: 52 };
const WF_HEIGHT = 380;

function WaterfallChart({ data }: { readonly data: TumorRow[] }) {
  const { ref, width } = useContainerWidth();
  const svgRef = useRef<SVGSVGElement>(null);

  const rows = useMemo(
    () =>
      data
        .filter(
          (d) => d.best_pct_change !== null && d.best_pct_change !== undefined,
        )
        .map((d) => ({
          subject: d.subject ?? "",
          value: Number(d.best_pct_change),
          bor: d.best_overall_response ?? "",
        }))
        .filter((d) => Number.isFinite(d.value))
        .sort((a, b) => b.value - a.value),
    [data],
  );

  useEffect(() => {
    const el = svgRef.current;
    if (!el || width === 0) return;
    const w = width - WF_MARGIN.left - WF_MARGIN.right;
    const h = WF_HEIGHT - WF_MARGIN.top - WF_MARGIN.bottom;

    const svg = d3.select(el);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", WF_HEIGHT);
    const g = svg
      .append("g")
      .attr("transform", `translate(${WF_MARGIN.left},${WF_MARGIN.top})`);

    if (!rows.length) {
      g.append("text")
        .attr("x", w / 2)
        .attr("y", h / 2)
        .attr("text-anchor", "middle")
        .attr("fill", "#5b6b73")
        .text("No best-% -change data available.");
      return;
    }

    const x = d3
      .scaleBand<number>()
      .domain(rows.map((_, i) => i))
      .range([0, w])
      .paddingInner(0.2);

    const maxAbs = d3.max(rows, (d) => Math.abs(d.value)) ?? 100;
    const yMax = Math.max(30, Math.ceil((maxAbs + 5) / 10) * 10);
    const y = d3.scaleLinear().domain([-100, yMax]).range([h, 0]).clamp(true);

    // Y axis + gridlines.
    g.append("g")
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickFormat((d) => `${d}%`),
      )
      .call((sel) => sel.select(".domain").attr("stroke", "#d5dbde"))
      .call((sel) => sel.selectAll(".tick line").attr("stroke", "#eef1f2"))
      .call((sel) =>
        sel.selectAll("text").attr("fill", "#5b6b73").attr("font-size", 11),
      );

    g.append("g")
      .selectAll("line.grid")
      .data(y.ticks(6))
      .join("line")
      .attr("class", "grid")
      .attr("x1", 0)
      .attr("x2", w)
      .attr("y1", (d) => y(d))
      .attr("y2", (d) => y(d))
      .attr("stroke", "#eef1f2");

    // RECIST reference lines: +20% (PD) and -30% (PR).
    const refLine = (value: number, color: string, label: string) => {
      g.append("line")
        .attr("x1", 0)
        .attr("x2", w)
        .attr("y1", y(value))
        .attr("y2", y(value))
        .attr("stroke", color)
        .attr("stroke-dasharray", "4 3")
        .attr("stroke-width", 1);
      g.append("text")
        .attr("x", w - 2)
        .attr("y", y(value) - 3)
        .attr("text-anchor", "end")
        .attr("fill", color)
        .attr("font-size", 10)
        .text(label);
    };
    refLine(20, "#e34948", "+20% progression");
    refLine(-30, "#127a52", "-30% response");

    // Zero baseline.
    g.append("line")
      .attr("x1", 0)
      .attr("x2", w)
      .attr("y1", y(0))
      .attr("y2", y(0))
      .attr("stroke", "#b7c0c4");

    const tooltip = d3
      .select(ref.current)
      .append("div")
      .attr(
        "class",
        "pointer-events-none absolute z-50 rounded bg-[#2B4752] px-2 py-1 text-xs text-white opacity-0",
      );

    g.selectAll("rect.bar")
      .data(rows)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (_, i) => x(i) ?? 0)
      .attr("width", x.bandwidth())
      .attr("y", (d) => (d.value >= 0 ? y(d.value) : y(0)))
      .attr("height", (d) => Math.abs(y(d.value) - y(0)))
      .attr("fill", (d) => borColor(d.bor))
      .on("mouseover", (_, d) =>
        tooltip
          .style("opacity", 1)
          .html(
            `<div><strong>${d.subject}</strong></div>` +
              `<div>Best % change: ${d.value.toFixed(1)}%</div>` +
              `<div>BOR: ${d.bor || "—"}</div>`,
          ),
      )
      .on("mousemove", (event) => {
        const [mx, my] = d3.pointer(event, ref.current);
        tooltip.style("left", `${mx + 12}px`).style("top", `${my - 8}px`);
      })
      .on("mouseout", () => tooltip.style("opacity", 0));

    // Y axis title.
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -h / 2)
      .attr("y", -WF_MARGIN.left + 12)
      .attr("text-anchor", "middle")
      .attr("fill", "#5b6b73")
      .attr("font-size", 11)
      .text("Best % change in sum of diameters");

    return () => {
      tooltip.remove();
    };
  }, [rows, width, ref]);

  return (
    <div>
      <SectionHeader
        title="Waterfall — best % change from baseline"
        subtitle="One bar per subject, sorted best-to-worst, coloured by best overall response"
      />
      <div
        ref={ref}
        className="relative w-full bg-white rounded-xs shadow-omni p-4"
      >
        <svg ref={svgRef} className="w-full" />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- swimlane */

const SL_MARGIN = { top: 24, right: 90, bottom: 32, left: 132 };
const SL_LANE = 9;

function PfsSwimlane({ data }: { readonly data: TumorRow[] }) {
  const { ref, width } = useContainerWidth();
  const svgRef = useRef<SVGSVGElement>(null);

  const rows = useMemo(
    () =>
      data
        .filter((d) => d.pfs_days !== null && d.pfs_days !== undefined)
        .map((d) => ({
          subject: d.subject ?? "",
          pfs: Number(d.pfs_days),
          bor: d.best_overall_response ?? "",
          ongoing: d.treatment_ongoing === true,
        }))
        .filter((d) => Number.isFinite(d.pfs))
        .sort((a, b) => b.pfs - a.pfs),
    [data],
  );

  const height = SL_MARGIN.top + SL_MARGIN.bottom + rows.length * SL_LANE;

  useEffect(() => {
    const el = svgRef.current;
    if (!el || width === 0) return;
    const w = width - SL_MARGIN.left - SL_MARGIN.right;
    const h = Math.max(1, rows.length * SL_LANE);

    const svg = d3.select(el);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height);
    const g = svg
      .append("g")
      .attr("transform", `translate(${SL_MARGIN.left},${SL_MARGIN.top})`);

    if (!rows.length) {
      g.append("text")
        .attr("fill", "#5b6b73")
        .text("No progression-free-survival data available.");
      return;
    }

    const x = d3
      .scaleLinear()
      .domain([0, d3.max(rows, (d) => d.pfs) ?? 1])
      .nice()
      .range([0, w]);
    const yb = d3
      .scaleBand<number>()
      .domain(rows.map((_, i) => i))
      .range([0, h])
      .paddingInner(0.25);

    // X axis (days).
    g.append("g")
      .attr("transform", `translate(0,${h})`)
      .call(d3.axisBottom(x).ticks(8))
      .call((sel) => sel.select(".domain").attr("stroke", "#d5dbde"))
      .call((sel) => sel.selectAll(".tick line").attr("stroke", "#d5dbde"))
      .call((sel) =>
        sel.selectAll("text").attr("fill", "#5b6b73").attr("font-size", 11),
      );
    g.append("text")
      .attr("x", w / 2)
      .attr("y", h + 28)
      .attr("text-anchor", "middle")
      .attr("fill", "#5b6b73")
      .attr("font-size", 11)
      .text("Progression-free survival (days)");

    const tooltip = d3
      .select(ref.current)
      .append("div")
      .attr(
        "class",
        "pointer-events-none absolute z-50 rounded bg-[#2B4752] px-2 py-1 text-xs text-white opacity-0",
      );

    const lanes = g
      .selectAll("g.lane")
      .data(rows)
      .join("g")
      .attr("class", "lane")
      .attr("transform", (_, i) => `translate(0,${yb(i) ?? 0})`)
      .on("mouseover", (_, d) =>
        tooltip
          .style("opacity", 1)
          .html(
            `<div><strong>${d.subject}</strong></div>` +
              `<div>PFS: ${d.pfs} days</div>` +
              `<div>BOR: ${d.bor || "—"}</div>` +
              `<div>${d.ongoing ? "On treatment ▶" : "Off treatment"}</div>`,
          ),
      )
      .on("mousemove", (event) => {
        const [mx, my] = d3.pointer(event, ref.current);
        tooltip.style("left", `${mx + 12}px`).style("top", `${my - 8}px`);
      })
      .on("mouseout", () => tooltip.style("opacity", 0));

    lanes
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", (d) => Math.max(1, x(d.pfs)))
      .attr("height", yb.bandwidth())
      .attr("fill", (d) => borColor(d.bor));

    // Ongoing-treatment marker: a small right-pointing triangle at the lane end.
    lanes
      .filter((d) => d.ongoing)
      .append("path")
      .attr("d", d3.symbol(d3.symbolTriangle, 26))
      .attr("fill", "#2B4752")
      .attr(
        "transform",
        (d) => `translate(${x(d.pfs) + 6},${yb.bandwidth() / 2}) rotate(90)`,
      );

    return () => {
      tooltip.remove();
    };
  }, [rows, width, height, ref]);

  return (
    <div>
      <SectionHeader
        title="Swimlane — progression-free survival"
        subtitle="One lane per subject, length = PFS days, coloured by BOR; ▶ = still on treatment"
      />
      <div
        ref={ref}
        className="relative w-full max-h-[560px] overflow-y-auto bg-white rounded-xs shadow-omni p-4"
      >
        <svg ref={svgRef} className="w-full" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- shared */

function SectionHeader({
  title,
  subtitle,
}: {
  readonly title: string;
  readonly subtitle?: string;
}) {
  return (
    <div className="mb-2">
      <h3 className="text-base font-semibold text-[#2B4752]">{title}</h3>
      {subtitle && <p className="text-sm text-[#5b6b73]">{subtitle}</p>}
    </div>
  );
}

function BorLegend() {
  return (
    <div className="flex flex-wrap gap-4 text-sm text-[#5b6b73]">
      {BOR_ORDER.map((bor) => (
        <span key={bor} className="inline-flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: BOR_COLORS[bor] }}
          />
          {bor} — {BOR_LABELS[bor]}
        </span>
      ))}
    </div>
  );
}
